import type { Language } from '@offer-catalog/services/i18n'
import type { Material, ProductPart } from '@/api/ddfs/ddfs'
import type { ProductComposition as ProductCompositionType } from '@/components/ProductCard/types'
import type { CollectionResponse } from '@/types/bff'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { fetchDdfsCollection } from '@/api/ddfs/ddfs'
import { queryKeys } from '@/query/queryKeys'
import { DataFallback } from './DataFallback'

export function ProductCompositionItem({ item, product_parts }: { item: ProductCompositionType, product_parts: CollectionResponse<ProductPart> | undefined }) {
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language.split('-')[0] as Language
  const ids = item.part_of_product?.raw_materials.map(rawMaterial => rawMaterial?.ddfs_id) ?? []

  const { data: raw_materials } = useQuery({
    enabled: !!item.part_of_product?.raw_materials?.length,
    queryKey: queryKeys.rawMaterials(ids, currentLanguage),
    queryFn: async () => {
      return await fetchDdfsCollection<Material>('raw_materials', { 'id[]': ids }, currentLanguage)
    },
  })

  const hasRawMaterials = !!item.part_of_product?.raw_materials?.length

  return product_parts?.items.map(part => (
    <div key={part.id}>
      {part.name}
      {' '}
      {t('products-page.product-card.composition-product-rate', { rate: item.part_of_product?.rate })}
      {hasRawMaterials && (
        <>
          :
          {' '}
          {raw_materials?.items.map(material => material.name).join(', ')}
        </>
      )}
    </div>
  ))
}

export function ProductComposition({ composition }: Readonly<{ composition: ProductCompositionType[] }>) {
  const { i18n, t } = useTranslation()
  const currentLanguage = i18n.language.split('-')[0] as Language
  const ids = composition?.map(item => item.part_of_product?.ddfs_id)

  const { data: product_parts } = useQuery({
    enabled: !!ids?.length,
    queryKey: queryKeys.partOfProducts(ids, currentLanguage),
    queryFn: async () => {
      return await fetchDdfsCollection<ProductPart>('product_parts', { 'id[]': ids }, currentLanguage)
    },
    retry: false,
  })

  return (
    <div className="product-composition" data-testid="product_composition">
      <span className="sr-only">
        {t('products-page.custom-view-panel.fields.composition')}
        :
        {' '}
      </span>
      <DataFallback
        value={composition}
        fallbackKey="products-page.product-card.no-composition"
        isEmpty={items => items.length === 0}
      >
        {items => items.map(item => (
          <ProductCompositionItem key={item.part_of_product?.id} item={item} product_parts={product_parts} />
        ))}
      </DataFallback>
    </div>
  )
}
